import "./ChatSpeakingCard.css";
import { toast } from "@ui/molecules/Toasts/toast";
import {
	createEffect,
	createMemo,
	createSignal,
	createUniqueId,
	For,
	Match,
	onCleanup,
	Show,
	Switch,
} from "solid-js";
import { formatMarkdown, htmlToPlainText } from "~/modules/shared/utils/text";
import { ArtifactMention } from "../../components/ArtifactMention.tsx";
import { useOffersClipboard } from "../../components/clipboard/offersClipboard.tsx";
import type { MentionedVideoItem } from "../../components/VideoMention.tsx";
import type { SpeakingBlockSegment } from "../../utils/parseSpeakingBlockSegments.ts";
import {
	markdownForSpeakingSegment,
	parseSpeakingBlockSegments,
	reconcileSpeakingBlockSegments,
} from "../../utils/parseSpeakingBlockSegments.ts";
import { insertMentionedVideoBlocksSync } from "../../utils/richTextDomHydration.tsx";
import type { PlayVideoMentionsHandler } from "../../utils/videoMentionPlayback.ts";
import { getCardMetadata } from "../cardMetadata.ts";
import type { SpeakingBlockState } from "../chatPanelState.ts";
import { ChatCardToolbar } from "./ChatCardToolbar.tsx";
import { SpeakingMarkdownSegment } from "./SpeakingMarkdownSegment.tsx";

declare const pageVars: {
	appEmbedded: boolean;
};

function buildClipboardContent(block: SpeakingBlockState): {
	plainText: string;
	html: string;
} {
	let plainTextSource = block.markdown;
	let htmlSource = block.markdown;

	for (const [id, entry] of Object.entries(block.mentions)) {
		if (entry.kind === "mention") {
			plainTextSource = plainTextSource.replace(id, `@${entry.data.handle}`);
			htmlSource = htmlSource.replace(
				id,
				`[@${entry.data.handle}](https://${entry.data.platform ?? "instagram"}.com/${entry.data.handle})`,
			);
		} else if (entry.kind === "video") {
			const replacement = `Video ${entry.data.start_time_sec} - ${entry.data.end_time_sec} seconds`;
			plainTextSource = plainTextSource.replace(id, replacement);
			htmlSource = htmlSource.replace(id, replacement);
		} else {
			// Artifacts (e.g. video carousels) have no meaningful text form; drop
			// the placeholder from copied content.
			plainTextSource = plainTextSource.replace(id, "");
			htmlSource = htmlSource.replace(id, "");
		}
	}

	const html = formatMarkdown(htmlSource);
	const plainTextEl = document.createElement("div");
	plainTextEl.innerHTML = formatMarkdown(plainTextSource);
	const plainText = htmlToPlainText(plainTextEl);

	return { plainText, html };
}

function videoMentionsByIdFromBlock(
	block: SpeakingBlockState,
): Map<string, MentionedVideoItem> {
	const map = new Map<string, MentionedVideoItem>();
	for (const entry of Object.values(block.mentions)) {
		if (entry.kind === "video") {
			map.set(String(entry.data.id), entry.data);
		}
	}
	return map;
}

type Props = {
	block: SpeakingBlockState;
	interactionId: string;
	questionId: string;
	projectId?: string;
	artifactId?: string;
	/** Autosaved registry document for the interaction transcript artifact. */
	documentId?: string;
	isFavourite?: boolean;
	/** Plays mentioned videos in the host's own player instead of the chat's video panel. */
	onPlayVideoMentions?: PlayVideoMentionsHandler;
};

type SegmentViewProps = {
	segment: SpeakingBlockSegment;
	block: SpeakingBlockState;
	interactionId: string;
	questionId: string;
	projectId?: string;
	idBase: string;
	onMentionedVideosDomUpdated: () => void;
	onPlayVideoMentions?: PlayVideoMentionsHandler;
};

const SpeakingBlockSegmentView = (props: SegmentViewProps) => (
	<Switch>
		<Match
			when={
				props.segment.kind === "markdown" &&
				markdownForSpeakingSegment(props.block, props.segment.key).length > 0
			}
		>
			<SpeakingMarkdownSegment
				block={props.block}
				segmentKey={props.segment.key}
				interactionId={props.interactionId}
				idBase={props.idBase}
				onMentionedVideosDomUpdated={props.onMentionedVideosDomUpdated}
				onPlayVideoMentions={props.onPlayVideoMentions}
			/>
		</Match>
		<Match when={props.segment.kind === "artifact" ? props.segment : undefined}>
			{(segment) => (
				<ArtifactMention
					artifact={segment().artifact}
					appEmbedded={pageVars.appEmbedded}
					interactionId={props.interactionId}
					questionId={props.questionId}
					projectId={props.projectId}
				/>
			)}
		</Match>
	</Switch>
);

export const ChatSpeakingCard = (props: Props) => {
	const idBase = createUniqueId();
	const offersClipboard = useOffersClipboard();
	const videoBlockDisposers: Array<() => void> = [];
	const [contentContainer, setContentContainer] = createSignal<
		HTMLElement | undefined
	>();
	let stableSegments: SpeakingBlockSegment[] = [];
	const segments = createMemo(() => {
		const parsed = parseSpeakingBlockSegments(props.block);
		stableSegments = reconcileSpeakingBlockSegments(stableSegments, parsed);
		return [...stableSegments];
	});

	const placeMentionedVideoBlocks = () => {
		const el = contentContainer();
		const block = props.block;
		if (!el) {
			return;
		}

		for (const dispose of videoBlockDisposers.splice(0)) {
			dispose();
		}

		const videoMentionsById = videoMentionsByIdFromBlock(block);
		insertMentionedVideoBlocksSync(el, {
			resolveVideoMention: (id) => videoMentionsById.get(id),
			appEmbedded: pageVars.appEmbedded,
			offersClipboard: offersClipboard(),
			answerComplete: block.complete,
			onPlayVideoMentions: props.onPlayVideoMentions,
			disposers: videoBlockDisposers,
		});
	};

	let placementQueued = false;
	let disposed = false;
	/**
	 * Streamed tables set their rows through signals, which only reach the DOM
	 * once the update that set them has finished, so the pass waits for that;
	 * run any sooner and it misses the mentions in the latest rows.
	 */
	const scheduleMentionedVideoBlocks = () => {
		if (placementQueued) {
			return;
		}
		placementQueued = true;
		queueMicrotask(() => {
			placementQueued = false;
			if (!disposed) {
				placeMentionedVideoBlocks();
			}
		});
	};

	createEffect(() => {
		const el = contentContainer();
		segments();
		// The pass itself runs untracked, so read what it depends on here.
		props.block.complete;
		videoMentionsByIdFromBlock(props.block);
		if (!el) {
			return;
		}

		scheduleMentionedVideoBlocks();
	});

	onCleanup(() => {
		disposed = true;
		for (const dispose of videoBlockDisposers.splice(0)) {
			dispose();
		}
	});

	const cardMetadata = () => getCardMetadata(props.block.answerType);
	const cardType = () => props.block.answerType.replace("_", " ");
	const clipboard = () => buildClipboardContent(props.block);

	return (
		<section data-type="speaking">
			<div ref={setContentContainer} class="speaking-card-content">
				<For each={segments()}>
					{(segment) => (
						<SpeakingBlockSegmentView
							segment={segment}
							block={props.block}
							interactionId={props.interactionId}
							questionId={props.questionId}
							projectId={props.projectId}
							idBase={idBase}
							onMentionedVideosDomUpdated={scheduleMentionedVideoBlocks}
							onPlayVideoMentions={props.onPlayVideoMentions}
						/>
					)}
				</For>
			</div>
			<Show when={props.block.complete}>
				<ChatCardToolbar
					questionId={props.questionId}
					interactionId={props.interactionId}
					ratingSubject="this answer"
					cardType={cardType()}
					cardTypeSymbol={cardMetadata().symbol}
					artifactId={props.artifactId}
					documentId={props.documentId}
					isFavourite={props.isFavourite}
					padTop
					onCopy={() => {
						const { plainText, html } = clipboard();
						navigator.clipboard.write([
							new ClipboardItem({
								"text/plain": new Blob([plainText], { type: "text/plain" }),
								"text/html": new Blob([html], { type: "text/html" }),
							}),
						]);
						toast.success({ title: "Copied to clipboard" });
					}}
				/>
			</Show>
		</section>
	);
};
